import React, { useMemo } from "react";
import { Navigate, type DateLocalizer } from "react-big-calendar";
// @ts-expect-error TimeGrid is not exported in package types
import TimeGrid from "react-big-calendar/lib/TimeGrid";
import { addDays, format } from "date-fns";
import { es } from "date-fns/locale";
import { getThreeDayRange } from "./threeDayView.constants";

const THREE_DAY_COUNT = 3;

interface ThreeDayViewProps {
  date: Date;
  localizer: DateLocalizer;
  max?: Date;
  min?: Date;
  scrollToTime?: Date;
  [key: string]: unknown;
}

/**
 * Rolling three-day time grid for react-big-calendar on mobile viewports.
 */
function ThreeDayView({
  date,
  localizer,
  max = localizer.endOf(new Date(), "day"),
  min = localizer.startOf(new Date(), "day"),
  scrollToTime = localizer.startOf(new Date(), "day"),
  ...props
}: ThreeDayViewProps) {
  const range = useMemo(() => getThreeDayRange(date), [date]);

  return (
    <TimeGrid
      {...props}
      date={date}
      eventOffset={15}
      localizer={localizer}
      max={max}
      min={min}
      range={range}
      scrollToTime={scrollToTime}
    />
  );
}

ThreeDayView.range = (date: Date) => getThreeDayRange(date);

ThreeDayView.navigate = (date: Date, action: string) => {
  switch (action) {
    case Navigate.PREVIOUS:
      return addDays(date, -THREE_DAY_COUNT);
    case Navigate.NEXT:
      return addDays(date, THREE_DAY_COUNT);
    default:
      return date;
  }
};

ThreeDayView.title = (date: Date) => {
  const [start, , end] = getThreeDayRange(date);
  const sameMonth = start.getMonth() === end.getMonth();

  if (sameMonth) {
    return `${format(start, "d", { locale: es })} – ${format(end, "d MMM", { locale: es })}`;
  }

  return `${format(start, "d MMM", { locale: es })} – ${format(end, "d MMM", { locale: es })}`;
};

export default ThreeDayView;
